import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useHiringReviewApplicationMutation, useHiringRejectApplicationMutation } from "@/features/hr/hiring";
import { extractErrorMessage, HR_FILL_CONTRACT_PAGE_ROUTE } from "@/shared/utils";
import type { PromptState } from "./useHiringModals";

export interface UseHiringMutationsReturn {
  reviewApplication: ReturnType<typeof useHiringReviewApplicationMutation>[0];
  rejectApplication: ReturnType<typeof useHiringRejectApplicationMutation>[0];
  isLoading: {
    isReviewing: boolean;
    isRejecting: boolean;
    isCreatingContract: boolean;
    isLoadingAny: boolean;
  };
  reviewingApplicationId: number | null;
  rejectingApplicationId: number | null;
  handleApprove: (id: number, onClose?: () => void) => Promise<void>;
  handleSendRevision: (id: number, notes: string) => Promise<void>;
  handleSendReject: (id: number, reason: string) => Promise<void>;
  handleCreateContract: (id: number) => void;
  setPrompt: (state: PromptState | null) => void;
}

type SuccessAction = "approve" | "revision" | "reject" | null;

export function useHiringMutations(
  setPrompt: (state: PromptState | null) => void,
  isFetching?: boolean,
): UseHiringMutationsReturn {
  const { t } = useTranslation("HiringPage");
  const navigate = useNavigate();

  const [reviewApplication] = useHiringReviewApplicationMutation();
  const [rejectApplication] = useHiringRejectApplicationMutation();

  const [reviewingApplicationId, setReviewingApplicationId] = useState<number | null>(null);
  const [rejectingApplicationId, setRejectingApplicationId] = useState<number | null>(null);

  const isReviewing = reviewingApplicationId !== null;
  const isRejecting = rejectingApplicationId !== null;
  const isCreatingContract = false;
  const isLoadingAny = isReviewing || isRejecting || isCreatingContract;

  const [successAction, setSuccessAction] = useState<SuccessAction>(null);
  const sawFetchingRef = useRef(false);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPromptRef = useRef<PromptState | null>(null);

  const finishAfterRefetch = useCallback(
    function finishAfterRefetch() {
      if (successAction === "approve" || successAction === "revision") {
        setReviewingApplicationId(null);
      } else if (successAction === "reject") {
        setRejectingApplicationId(null);
      }

      if (pendingPromptRef.current) {
        setPrompt(pendingPromptRef.current);
        pendingPromptRef.current = null;
      }

      setSuccessAction(null);
      sawFetchingRef.current = false;

      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }
    },
    [successAction, setPrompt],
  );

  useEffect(
    function watchRefetchCompletion() {
      if (successAction === null) {
        sawFetchingRef.current = false;
        return;
      }
      if (isFetching) {
        sawFetchingRef.current = true;
        return;
      }
      if (sawFetchingRef.current) {
        finishAfterRefetch();
      }
    },
    [successAction, isFetching, finishAfterRefetch],
  );

  useEffect(
    function safetyTimeoutEffect() {
      if (successAction === null) return;

      safetyTimeoutRef.current = setTimeout(() => finishAfterRefetch(), 5000);

      return () => {
        if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
      };
    },
    [successAction, finishAfterRefetch],
  );

  async function handleApprove(id: number, onClose?: () => void) {
    if (isReviewing) return;

    try {
      setReviewingApplicationId(id);
      await reviewApplication({ id, action: "approve" }).unwrap();
      if (onClose) {
        onClose();
      }
      pendingPromptRef.current = {
        title: t("messages.approveSuccessTitle"),
        text: t("messages.approveSuccessText"),
        variant: "success",
      };
      setSuccessAction("approve");
    } catch (error) {
      if (onClose) {
        onClose();
      }
      setReviewingApplicationId(null);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.approveErrorText", t),
        variant: "error",
      });
    }
  }

  async function handleSendRevision(id: number, notes: string) {
    if (isReviewing) return;

    try {
      setReviewingApplicationId(id);
      await reviewApplication({
        id,
        action: "request_revision",
        notes,
      }).unwrap();

      pendingPromptRef.current = {
        title: t("messages.revisionSuccessTitle"),
        text: t("messages.revisionSuccessText"),
        variant: "success",
      };
      setSuccessAction("revision");
    } catch (error) {
      setReviewingApplicationId(null);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.revisionErrorText", t),
        variant: "error",
      });
    }
  }

  async function handleSendReject(id: number, reason: string) {
    if (isRejecting) return;

    try {
      setRejectingApplicationId(id);
      await rejectApplication({
        id,
        rejection_reason: reason,
      }).unwrap();

      pendingPromptRef.current = {
        title: t("messages.rejectSuccessTitle"),
        text: t("messages.rejectSuccessText"),
        variant: "success",
      };
      setSuccessAction("reject");
    } catch (error) {
      setRejectingApplicationId(null);
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.rejectErrorText", t),
        variant: "error",
      });
    }
  }

  function handleCreateContract(id: number) {
    navigate(HR_FILL_CONTRACT_PAGE_ROUTE.replace(":applicationId", String(id)));
  }

  return {
    reviewApplication,
    rejectApplication,
    isLoading: {
      isReviewing,
      isRejecting,
      isCreatingContract,
      isLoadingAny,
    },
    reviewingApplicationId,
    rejectingApplicationId,
    handleApprove,
    handleSendRevision,
    handleSendReject,
    handleCreateContract,
    setPrompt,
  };
}
